import { useEffect } from 'react';
import { useStore } from '@nanostores/react';
import {
  Badge,
  Banner,
  Card,
  EmptyState,
  HStack,
  Heading,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  Text,
  VStack,
} from '@astryxdesign/core';

import { formatDateTime, formatNumber, formatQuota, type DailyCount } from '../lib/api';
import { $requests, $usage, $usageError, $usageLoading, loadUsage } from '../stores/usage';

export function UsagePage() {
  const usage = useStore($usage);
  const requests = useStore($requests);
  const loading = useStore($usageLoading);
  const error = useStore($usageError);

  useEffect(() => {
    void loadUsage(30);
  }, []);

  if (loading && !usage) {
    return <Spinner size="lg" label="Loading usage" />;
  }

  return (
    <VStack gap={3}>
      <VStack gap={0.5}>
        <Heading level={1}>Usage</Heading>
        <Text color="secondary">
          {usage ? `Billing period ${usage.period} (UTC).` : 'Usage for the current billing period.'}
        </Text>
      </VStack>

      {error && <Banner status="error" title="Could not load usage" description={error} />}

      {usage && (
        <>
          <div className="stat-grid">
            <Stat label="Calls" value={formatNumber(usage.current.requests)} />
            <Stat label="Quota" value={formatQuota(usage.quota)} />
            <Stat label="Remaining" value={usage.quota === 0 ? '∞' : formatNumber(usage.remaining)} />
            <Stat label="Series forecast" value={formatNumber(usage.current.series)} />
            <Stat label="Forecast points" value={formatNumber(usage.current.forecast_points)} />
            <Stat label="Errors" value={formatNumber(usage.current.errors)} />
            <Stat label="Throttled (429)" value={formatNumber(usage.current.throttled_count)} />
            <Stat
              label="Model time"
              value={`${(usage.current.inference_millis / 1000).toFixed(1)}s`}
            />
          </div>

          <Card padding={3} variant="default">
            <VStack gap={2}>
              <Text weight="semibold">Last 30 days</Text>
              {usage.daily.length === 0 ? (
                <Text size="sm" color="secondary">
                  No requests recorded in this window yet.
                </Text>
              ) : (
                <DailyBars daily={usage.daily} />
              )}
            </VStack>
          </Card>

          {usage.history.length > 1 && (
            <Card padding={3} variant="default">
              <VStack gap={2}>
                <Text weight="semibold">Monthly history</Text>
                <Table density="balanced" dividers="rows">
                  <TableHeader>
                    <TableRow>
                      <TableHeaderCell>Period</TableHeaderCell>
                      <TableHeaderCell>Calls</TableHeaderCell>
                      <TableHeaderCell>Series</TableHeaderCell>
                      <TableHeaderCell>Points</TableHeaderCell>
                      <TableHeaderCell>Errors</TableHeaderCell>
                      <TableHeaderCell>Throttled</TableHeaderCell>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {usage.history.map((h) => (
                      <TableRow key={h.period}>
                        <TableCell>
                          <span className="mono">{h.period}</span>
                        </TableCell>
                        <TableCell>{formatNumber(h.requests)}</TableCell>
                        <TableCell>{formatNumber(h.series)}</TableCell>
                        <TableCell>{formatNumber(h.forecast_points)}</TableCell>
                        <TableCell>{formatNumber(h.errors)}</TableCell>
                        <TableCell>{formatNumber(h.throttled_count)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </VStack>
            </Card>
          )}
        </>
      )}

      <Card padding={3} variant="default">
        <VStack gap={2}>
          <HStack gap={1} justify="between" vAlign="center" wrap="wrap">
            <Text weight="semibold">Recent requests</Text>
            <Text size="sm" color="secondary">
              Retained for 30 days
            </Text>
          </HStack>

          {requests.length === 0 ? (
            <EmptyState
              isCompact
              title="No requests yet"
              description="Calls to /v1/forecast will appear here within a second of completing."
            />
          ) : (
            <Table density="compact" dividers="rows" hasHover>
              <TableHeader>
                <TableRow>
                  <TableHeaderCell>When</TableHeaderCell>
                  <TableHeaderCell>Key</TableHeaderCell>
                  <TableHeaderCell>Route</TableHeaderCell>
                  <TableHeaderCell>Status</TableHeaderCell>
                  <TableHeaderCell>Series</TableHeaderCell>
                  <TableHeaderCell>Horizon</TableHeaderCell>
                  <TableHeaderCell>Latency</TableHeaderCell>
                </TableRow>
              </TableHeader>
              <TableBody>
                {requests.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell>
                      <Text size="sm" color="secondary">
                        {formatDateTime(r.created_at)}
                      </Text>
                    </TableCell>
                    <TableCell>
                      <span className="mono">{r.key_prefix}</span>
                    </TableCell>
                    <TableCell>
                      <span className="mono">{r.route}</span>
                    </TableCell>
                    <TableCell>
                      <Badge
                        label={String(r.status)}
                        variant={r.status < 400 ? 'success' : r.status < 500 ? 'warning' : 'error'}
                      />
                    </TableCell>
                    <TableCell>{r.series}</TableCell>
                    <TableCell>{r.horizon}</TableCell>
                    <TableCell>{r.latency_ms} ms</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </VStack>
      </Card>
    </VStack>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <Card padding={2} variant="muted">
      <VStack gap={0.5}>
        <Text size="xsm" color="secondary">
          {label}
        </Text>
        <Text size="lg" weight="semibold">
          {value}
        </Text>
      </VStack>
    </Card>
  );
}

/** Bar chart of daily call volume; errors are drawn on top of successes. */
function DailyBars({ daily }: { daily: DailyCount[] }) {
  const width = 720;
  const height = 180;
  const pad = { top: 10, right: 8, bottom: 26, left: 40 };
  const plotW = width - pad.left - pad.right;
  const plotH = height - pad.top - pad.bottom;

  const maxVal = Math.max(1, ...daily.map((d) => d.requests));
  const slot = plotW / daily.length;
  const barW = Math.max(2, Math.min(22, slot * 0.68));

  return (
    <div className="chart-wrap">
      <svg
        className="chart"
        viewBox={`0 0 ${width} ${height}`}
        preserveAspectRatio="xMidYMid meet"
        role="img"
        aria-label={`Daily request volume across ${daily.length} days, peak ${maxVal}`}
      >
        {[0, maxVal / 2, maxVal].map((t) => {
          const y = pad.top + (1 - t / maxVal) * plotH;
          return (
            <g key={t}>
              <line
                x1={pad.left}
                x2={width - pad.right}
                y1={y}
                y2={y}
                stroke="var(--color-border)"
                strokeWidth={1}
              />
              <text
                x={pad.left - 6}
                y={y + 4}
                textAnchor="end"
                fontSize={11}
                fill="var(--color-text-secondary)"
              >
                {Math.round(t)}
              </text>
            </g>
          );
        })}

        {daily.map((d, i) => {
          const x = pad.left + i * slot + (slot - barW) / 2;
          const h = (d.requests / maxVal) * plotH;
          const errH = (d.errors / maxVal) * plotH;
          return (
            <g key={d.day}>
              <title>{`${d.day}: ${d.requests} calls, ${d.errors} errors, ${Math.round(d.avg_latency_ms)} ms avg`}</title>
              <rect
                x={x}
                y={pad.top + plotH - h}
                width={barW}
                height={h}
                rx={2}
                fill="var(--color-icon-blue)"
                opacity={0.85}
              />
              {d.errors > 0 && (
                <rect
                  x={x}
                  y={pad.top + plotH - h}
                  width={barW}
                  height={Math.max(1, errH)}
                  rx={2}
                  fill="var(--color-error)"
                />
              )}
            </g>
          );
        })}

        {daily.length > 0 && (
          <>
            <text x={pad.left} y={height - 8} fontSize={11} fill="var(--color-text-secondary)">
              {daily[0]!.day}
            </text>
            <text
              x={width - pad.right}
              y={height - 8}
              textAnchor="end"
              fontSize={11}
              fill="var(--color-text-secondary)"
            >
              {daily[daily.length - 1]!.day}
            </text>
          </>
        )}
      </svg>
    </div>
  );
}
